import http from 'hub-http/clients/apiClient';
import { getElementXPath, getPortalId, isDevMode, isHTMLElement, Logger, simulateLoadingError } from 'whisper-base';
import { getWhisper } from 'whisper-core-api';
import { NudgeEventTypes } from './constants';
import { definitions } from './definitions';
import { clearPendingEvents, getPendingEvents, storePendingEvents } from './events-storage';
import { getButtonProperties } from './get-button-attributes';
import { getAnchorProperties } from './get-link-attributes';
import { getNudgePropertiesFromAttributes } from './get-nudge-properties-from-attributes';
import { NudgeStateManager } from './nudge-state-manager';
const LOG = new Logger('[whisper-nudge-tracking]');
const idleCallbackSupported = typeof window.requestIdleCallback === 'function' && typeof window.cancelIdleCallback === 'function';

/**
 * Factory function that sets up nudge tracking for UI components in the application.
 *
 * This function initializes event listeners, mutation observers, and periodic flushing mechanisms
 * to track user interactions and lifecycle events (rendered, dismissed, action taken) for a set of
 * predefined UI components. Tracked events are queued and periodically sent to the backend for analytics.
 *
 * The returned object exposes an `initialize` method, which starts the tracking system and returns a
 * shutdown function to clean up all listeners and flush any remaining events.
 *
 * @returns {WhisperFactory} An object with an `initialize` method to start tracking and a shutdown function for cleanup.
 */
function WhisperNudgeTracking() {
  const FLUSH_INTERVAL_TIME = 5000; // 5 seconds
  const FLUSH_MAX_IDLE_TIME = 2000; // 2 seconds

  const containsSeleniumCookie = () => document.cookie.includes('hs_selenium');

  /**
   * This session ID will be only used if the hubspot.sessionId is not available.
   */
  const localSessionId = crypto.randomUUID();
  let intervalId = -1;
  let observer = null;
  let idleCallbackId = null;
  let isUnloading = false;

  // Create NudgeStateManager instance with session ID
  const nudgeStateManager = new NudgeStateManager(getSessionId(), LOG);

  // This will be used to store the events that we want to send to the backend
  const events = new Map();

  /**
   * Gets a unique identity string for a NudgeEvent
   * The identity is used to track the event uniquely, this is done to avoid
   * storing duplicate events in the map
   */
  function getEventIdentity(event) {
    return JSON.stringify({
      type: event.type,
      xpath: event.xpath,
      url: event.url
    });
  }

  /**
   * Finds all elements in the DOM that match a definition's selector.
   *
   * @param def - The tracked component definition containing the selector
   * @returns An array of TrackedComponentMatch objects
   */
  function findElementsByDefinition(def) {
    const elements = [...document.querySelectorAll(def.selector)];
    return elements.map(element => ({
      def,
      element
    }));
  }

  /**
   * Processes matched elements and stores events for each match.
   * This centralizes the pattern used in multiple places throughout the code.
   *
   * @param matched - Array of tracked component matches
   * @param eventType - The type of event to store (rendered, dismissed, action taken)
   */
  function processMatchedElements(matched, eventType) {
    matched.forEach(item => {
      storeEvent(eventType, item.element, Object.assign({}, item.def.getNudgeProperties({
        eventType,
        element: item.element
      }), getNudgePropertiesFromAttributes(item.element)));

      // Track once the first mouse enter (as SEEN event)
      if (eventType === NudgeEventTypes.RENDERED) {
        item.element.addEventListener('mouseenter', e => trackIndividualElementMouseEnter(e, item.def), {
          once: true
        });
      }
    });
  }
  const actionComponentSelectors = [['button', getButtonProperties], ['a', getAnchorProperties], ['input[type="button"]', getButtonProperties]];

  /**
   * This method will track button clicks on all definitions that we are interested in
   * tracking. It will check if the button is inside a tracked component and if so,
   * it will log the event.
   *
   * @param event The event that was triggered
   */
  function trackClickEvent(event) {
    if (event.target && event.target instanceof HTMLElement) {
      for (const def of definitions) {
        // Check the closest element that matches the selector, for example,
        // if you click on a button that is inside a model that we want to track
        // this method will check all ancestors of the button until it finds the first
        // element that matches the selector
        const matchedElement = event.target.closest(def.selector);
        if (isHTMLElement(matchedElement)) {
          // Check each action component selector to see if the clicked element matches any
          for (const [selector, propertyGetter] of actionComponentSelectors) {
            const element = event.target.closest(selector);
            if (isHTMLElement(element)) {
              // Fetch the action component properties
              const actionComponentProperties = propertyGetter(element);
              if (!actionComponentProperties) {
                LOG.warn(`Element ${element.tagName} does not match any action component type`);
                continue;
              }

              // Store the event with the combined properties
              storeEvent(NudgeEventTypes.ACTION_TAKEN, element, Object.assign({}, def.getNudgeProperties({
                eventType: NudgeEventTypes.ACTION_TAKEN,
                element: matchedElement
              }), getNudgePropertiesFromAttributes(matchedElement)), actionComponentProperties);
              break; // Found a match, no need to check other selectors
            }
          }
        }
      }
    }
  }

  /**
   * This method will log the "SEEN" event on the provided
   * element with associated properties and attributes.
   * @param event The event that was triggered
   */
  function trackIndividualElementMouseEnter(event, def) {
    const matchedElement = event.currentTarget;

    // Store the event with required properties
    storeEvent(NudgeEventTypes.SEEN, matchedElement, Object.assign({}, def.getNudgeProperties({
      eventType: NudgeEventTypes.SEEN,
      element: matchedElement
    }), getNudgePropertiesFromAttributes(matchedElement)));
  }

  /**
   * Retrieves the application URL by extracting the host and pathname
   * from the current window's location.
   *
   * @returns {string} The concatenated host and pathname of the current URL.
   */
  function getApplicationUrl() {
    let targetWindow = window;
    try {
      if (window.top && window.top.location.href) {
        targetWindow = window.top;
      }
    } catch (error) {
      LOG.debug('Cross-origin access blocked, using current window location');
    }
    const url = new URL(targetWindow.location.href);
    return `${url.host}${url.pathname}`;
  }

  /**
   * Extracts the application name from the current URL's pathname.
   *
   * The function assumes that the application name is the first segment
   * of the pathname in the URL. If the pathname is empty or does not contain
   * any segments, an empty string is returned.
   *
   * @returns {string} The application name extracted from the URL's pathname,
   * or an empty string if no application name is found.
   */
  function getApplicationName() {
    let targetWindow = window;

    // For situations where we have an iframed application running whisper ideally we want
    // the application name from the main window. This code will handle situations where the
    // domain, port and protocol are the same and associate a new target window. For situations
    // where this access triggers a SecurityError, we will fallback to the current window location
    // and log a debug message to indicate the fallback.
    try {
      if (window.top && window.top.location.href) {
        targetWindow = window.top;
      }
    } catch (error) {
      LOG.error(`Cross-origin access blocked for ${window.location.href}, using current window location.`);
    }
    const url = new URL(targetWindow.location.href);
    return url.pathname.split('/').filter(Boolean)[0] || '';
  }

  /**
   * Retrieves the application's locale from the `lang` attribute of the HTML document's root element.
   * If the `lang` attribute is not set, it defaults to 'en-us'.
   *
   * @returns {string} The locale of the application, e.g., 'en-us'.
   */
  function getApplicationLocale() {
    return document.documentElement.lang || 'en-us';
  }

  /**
   * Retrieves the session ID for the current user session.
   * It first checks if the HubSpot session ID is available in the global `window` object.
   * If not, it falls back to a locally generated session ID.
   *
   * @returns {string}
   */
  function getSessionId() {
    var _hubspot;
    if ((_hubspot = window.hubspot) !== null && _hubspot !== void 0 && _hubspot.sessionId) {
      var _hubspot2;
      return (_hubspot2 = window.hubspot) === null || _hubspot2 === void 0 ? void 0 : _hubspot2.sessionId;
    }
    return localSessionId;
  }

  /**
   * This method will store the event in the events map. The event will be stored
   * as a JSON string and the key will be the stringified event. This is done to
   * avoid storing duplicate events in the map.
   *
   * @param type The type of event that we are tracking
   * @param element The element that triggered the event
   * @param nudge The properties of the event that we are tracking
   */
  function storeEvent(type, element, nudge, actionComponentProperties) {
    const eventTimestamp = Date.now();
    const event = {
      type,
      session_id: getSessionId(),
      from_automated_test: containsSeleniumCookie(),
      event_timestamp: eventTimestamp,
      url: getApplicationUrl(),
      app_name: getApplicationName(),
      locale: getApplicationLocale(),
      xpath: getElementXPath(element),
      nudge
    };
    if (actionComponentProperties) {
      event.action_component = actionComponentProperties;
    }

    // Set the event in the events map
    events.set(getEventIdentity(event), event);

    // Handle collision tracking
    nudgeStateManager.handleCollisionTracking(element, nudge.type, type);
  }

  /**
   * Core logic to check an HTML element against tracked component definitions.
   * It checks if the element itself matches a definition's selector, and then
   * checks its children based on a provided method.
   *
   * @param element - The HTML element to check.
   * @param getChildCheckMethod - A function that takes a definition and returns the appropriate child checking method.
   * @returns An array of TrackedComponentMatch objects.
   * @internal
   */
  function _checkElementAgainstDefinitions(element, getChildCheckMethod) {
    const matched = [];
    for (const def of definitions) {
      // Check if the element itself matches any definitions
      if (element.matches(def.selector)) {
        matched.push({
          def,
          element
        });
      }
      const shouldTrackChildMethod = getChildCheckMethod(def);
      if (shouldTrackChildMethod) {
        const children = element.querySelectorAll(def.selector);
        children.forEach(childNode => {
          if (childNode instanceof HTMLElement) {
            if (shouldTrackChildMethod({
              parent: element,
              child: childNode
            })) {
              matched.push({
                def,
                element: childNode
              });
            }
          }
        });
      }
    }
    return matched;
  }

  /**
   * Checks a given HTML element against tracked component definitions to find matches
   * for 'rendered' events. It identifies if the element itself or its children
   * (based on `shouldTrackRenderedForChild`) match any definitions.
   *
   * @param element - The HTML element to check.
   * @returns An array of TrackedComponentMatch objects for rendered elements.
   */
  function checkElementAgainstDefinitionsForRendered(element) {
    return _checkElementAgainstDefinitions(element, def => def.shouldTrackRenderedForChild);
  }

  /**
   * Checks a given HTML element against tracked component definitions to find matches
   * for 'dismissed' events. It identifies if the element itself or its children
   * (based on `shouldTrackDismissedForChild`) match any definitions.
   *
   * @param element - The HTML element to check.
   * @returns An array of TrackedComponentMatch objects for dismissed elements.
   */
  function checkElementAgainstDefinitionsForDismissed(element) {
    return _checkElementAgainstDefinitions(element, def => def.shouldTrackDismissedForChild);
  }

  /**
   * Sets up a `MutationObserver` to monitor changes in the DOM and trigger events
   * based on added or removed nodes. The observer listens for `childList` mutations
   * and processes nodes that match specific definitions.
   *
   * - For added nodes, it checks if the node matches a "rendered" definition and
   *   stores a "rendered" event with associated properties.
   * - For removed nodes, it checks if the node matches a "dismissed" definition and
   *   stores a "dismissed" event with associated properties.
   *
   * The observer is configured to monitor the entire document body, including
   * changes in its subtree. If the document body is not available at the time of
   * execution, the observer setup is deferred until the `DOMContentLoaded` event.
   *
   * @returns The configured `MutationObserver` instance.
   */
  function setupDOMChangeObserver() {
    LOG.info('Setting up DOM change observer');
    observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Check for added nodes
          mutation.addedNodes.forEach(node => {
            if (node instanceof HTMLElement) {
              const matched = checkElementAgainstDefinitionsForRendered(node);
              processMatchedElements(matched, NudgeEventTypes.RENDERED);
            }
          });

          // Check for removed nodes
          mutation.removedNodes.forEach(node => {
            if (node instanceof HTMLElement) {
              const matched = checkElementAgainstDefinitionsForDismissed(node);
              processMatchedElements(matched, NudgeEventTypes.DISMISSED);
            }
          });
        }
      }
    });
    if (document.body && document.readyState !== 'loading' && observer) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    } else {
      window.addEventListener('DOMContentLoaded', () => {
        if (observer) {
          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        }
      });
    }
    return observer;
  }

  /**
   * Sets up global event listeners for the application.
   *
   * Currently, this function adds a click event listener to the entire document
   * to track button click events. The event listener is configured to use the
   * capture phase, ensuring it intercepts events before they reach their target.
   *
   * Note: Ensure that `trackButtonClickEvent` is defined and handles the event appropriately.
   */
  function setupGlobalEventListeners() {
    LOG.info('Setting up global event listeners');
    document.addEventListener('click', trackClickEvent, {
      capture: true
    });
  }

  /**
   * Flushes queued events, logs them, and clears the event queue.
   * @param reason - A string describing why the flush is occurring (for logging).
   */
  function flushQueuedEvents(reason) {
    if (events.size > 0) {
      LOG.info(`Flushing ${events.size} events due to: ${reason}`);
      if (isDevMode()) {
        [...events.values()].forEach(event => LOG.debug(`Event: ${JSON.stringify(event, null, 2)}`));
      }
      const portalId = getPortalId();
      /**
       * If the portal ID is not found, we need to skip the flush since NudgeTracking endpoint is App Auth and portalId is required
       */
      if (!portalId) {
        var _getWhisper, _getWhisper$captureMe;
        (_getWhisper = getWhisper()) === null || _getWhisper === void 0 || (_getWhisper$captureMe = _getWhisper.captureMessage) === null || _getWhisper$captureMe === void 0 || _getWhisper$captureMe.call(_getWhisper, 'Portal ID not found, skipping nudge event flush', {
          level: 'warn'
        });
        events.clear();
        return;
      }
      if (isUnloading) {
        // Store events in sessionStorage when the page is unloading
        storePendingEvents([...events.values()], portalId);
        events.clear();
        return;
      }
      http.post(`/nudge/tracking/v1/nudge-event`, {
        data: {
          nudgeEvents: [...events.values()]
        },
        portalId,
        safeMode: true
      }).catch(error => {
        LOG.error('Failed to send nudge events', error);
      });
      events.clear();
    }
  }

  /**
   * Cancels a previously scheduled idle callback, if one exists.
   *
   * This function checks if an idle callback has been scheduled (i.e., `idleCallbackId` is not null)
   * and if the `cancelIdleCallback` method is available on the `window` object. If both conditions
   * are met, it cancels the idle callback and resets `idleCallbackId` to null.
   */
  function wipeFlushIdleCallback() {
    if (idleCallbackId !== null && idleCallbackSupported) {
      window.cancelIdleCallback(idleCallbackId);
      idleCallbackId = null;
    }
  }

  /**
   * Handles the document's visibility change event. If the document becomes hidden,
   * it cancels any pending idle callback for event flushing and flushes events immediately.
   */
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      LOG.info('Tab became hidden, flushing events immediately.');
      wipeFlushIdleCallback();
      flushQueuedEvents('visibility hidden');
    }
  };

  /**
   * Processes any pending events from the previous session
   */
  function processPendingEvents() {
    const pendingEvents = getPendingEvents();
    if (pendingEvents.length > 0) {
      LOG.info(`Found ${pendingEvents.length} pending events from previous session`);
      pendingEvents.forEach(event => events.set(getEventIdentity(event), event));
      clearPendingEvents();
    }
  }

  /**
   * Sets up a periodic event flushing mechanism.
   * It uses `requestIdleCallback` to schedule event flushing during browser idle periods,
   * with a fallback to direct flushing if `requestIdleCallback` is not supported.
   * Also sets up a listener for document visibility changes to flush events when the tab is hidden.
   */
  function setupEventFlushing() {
    LOG.info('Setting up event flushing');
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', () => {
      isUnloading = true;
    });

    // Check for any pending events from previous session
    processPendingEvents();
    intervalId = window.setInterval(() => {
      if (events.size === 0) {
        return;
      }
      if (idleCallbackSupported) {
        // Only schedule a new idle callback if one isn't already pending
        if (idleCallbackId === null) {
          LOG.info('Interval triggered: Scheduling event flush with requestIdleCallback.');
          idleCallbackId = window.requestIdleCallback(() => {
            try {
              flushQueuedEvents('idle callback');
            } finally {
              wipeFlushIdleCallback();
            }
          }, {
            timeout: FLUSH_MAX_IDLE_TIME
          });
        } else {
          LOG.debug('Interval triggered: Event flush already scheduled via requestIdleCallback.');
        }
      } else {
        // Fallback for browsers that don't support requestIdleCallback
        LOG.debug('Interval triggered: Flushing events directly (requestIdleCallback not supported).');
        flushQueuedEvents('interval direct (fallback)');
      }
    }, FLUSH_INTERVAL_TIME);
  }

  /**
   * Installs the nudge tracking system by checking for existing tracked components,
   * setting up DOM mutation observers, global event listeners, and event flushing.
   *
   * This function ensures that all tracked component definitions are processed to find
   * matching elements in the DOM and logs their initial rendered events. It also sets up
   * the necessary observers and listeners to track future user interactions and component
   * lifecycle events. Installation occurs immediately if the document is ready, or after
   * the DOMContentLoaded event otherwise.
   */
  function install() {
    function checkDefinitions() {
      const allMatched = [];
      definitions.forEach(def => {
        const matches = findElementsByDefinition(def);
        allMatched.push(...matches);
      });
      console.log(`Found ${allMatched.length} elements matching tracked definitions.`);
      processMatchedElements(allMatched, NudgeEventTypes.RENDERED);
    }
    function _install() {
      LOG.info('Installing Whisper Nudge Tracking');

      // For testing purposes optionally simulate a loading error
      simulateLoadingError('NudgeTracking');

      // Check definitions immediately
      checkDefinitions();

      // Set up the DOM change observer
      setupDOMChangeObserver();

      // Set up global event listeners
      setupGlobalEventListeners();

      // Set up event flushing
      setupEventFlushing();
    }

    // If the document is already loaded or interactive, install immediately
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      _install();
    } else {
      window.addEventListener('DOMContentLoaded', () => {
        _install();
      });
    }
  }
  return {
    initialize() {
      install();
      return [{
        canDisplayNudges() {
          return nudgeStateManager.canDisplayNudges();
        }
      }, function uninstall() {
        LOG.info('Shutting down Whisper Nudge Tracking');

        // Flush any remaining events on shutdown
        flushQueuedEvents('shutdown');

        // Clear the interval if it is set
        if (intervalId !== -1) {
          clearInterval(intervalId);
          intervalId = -1; // Mark as cleared
        }

        // Remove the event listeners
        document.removeEventListener('click', trackClickEvent, {
          capture: true
        });

        // Disconnect the observer
        if (observer) {
          observer.disconnect();
          observer = null;
        }

        // Remove visibility change listener
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        wipeFlushIdleCallback();
        nudgeStateManager.cleanup();
      }];
    }
  };
}
export default WhisperNudgeTracking;